import { useItemSet } from '@exam-management/common/item-set-provider';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { ItemSetQuestionPreviewDialog, QuestionPreviewNavigationFooter } from '../../components';
import { useQuestionPreviewNavigation } from '../../hooks';
import { type ItemSetQuestionDetailFormInput } from '../item-set-question-edit-form';

interface FormItemSetQuestionPreviewModalProps {
  open: boolean;
  onClose: () => void;
}

export default function FormItemSetQuestionPreviewModal(
  props: Readonly<FormItemSetQuestionPreviewModalProps>,
) {
  const { open, onClose } = props;

  const { itemSet } = useItemSet();
  const { watch } = useFormContext<ItemSetQuestionDetailFormInput>();

  const { itemSetId, subQuestions, totalQuestions } = useMemo(
    () => ({
      itemSetId: itemSet.id,
      subQuestions: itemSet.questions ?? [],
      totalQuestions: itemSet.totalQuestions ?? 0,
    }),
    [itemSet, watch],
  );

  const {
    currentQuestionOrder,
    goToNextQuestion,
    goToPreviousQuestion,
    resetCurrentQuestionOrder,
  } = useQuestionPreviewNavigation(totalQuestions);

  const paragraph = watch('details.paragraph');

  const currentSubQuestion = useMemo<{
    id: string;
    details: string;
    choices: { label: string; order: number; isCorrect: boolean }[];
  }>(() => {
    const currentQuestion = subQuestions[currentQuestionOrder - 1];
    return {
      id: currentQuestion.id,
      details: currentQuestion.details,
      choices: currentQuestion.metadata.choices.map((choice) => ({
        label: choice.label,
        order: choice.order,
        isCorrect: choice.value === currentQuestion.correctAnswer.value,
      })),
    };
  }, [subQuestions, currentQuestionOrder]);

  return (
    <ItemSetQuestionPreviewDialog
      open={open}
      onClose={onClose}
      itemSetId={itemSetId}
      paragraph={paragraph}
      question={currentSubQuestion.details}
      questionId={currentSubQuestion.id}
      choices={currentSubQuestion.choices}
      totalQuestions={totalQuestions}
      currentQuestionOrder={currentQuestionOrder}
      slotProps={{ transition: { onExited: resetCurrentQuestionOrder } }}
      footer={
        <QuestionPreviewNavigationFooter
          onNextQuestion={goToNextQuestion}
          onBackQuestion={goToPreviousQuestion}
          totalQuestion={totalQuestions}
          currentQuestionOrder={currentQuestionOrder}
        />
      }
    />
  );
}
